// @section 1 Campaigns / Daily ("Yesterday's Report") shipped 2026-06-04.
// Single-day snapshot across four cards. Server resolves the effective AZ date
// when no ?date= is provided (latest day with sends in
// analytics.outreach_send_facts). Future dates are blocked by the date picker
// (max = today AZ) and the server (400 future_date_blocked) as a safety net.
//
// Cards:
//   1. Cold Email (LIVE)   sent / opens / open_pct / human_replies / other_replies
//   2. GA4 Engagement (LIVE) human_visits / other_visits / human_pct
//                              (analytics.outreach_ga4_sessions, demo4 only)
//   3. Social (TBD)        placeholder until row-per-post source exists
//   4. Websites (TBD)      placeholder until BigQuery export covers the 4
//                              marketing sites (signal-rev / navipoint-technologies
//                              / vertis-group / vertisholdings)

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  getCampaignDailySnapshot,
  CampaignsUnconfigured,
  isTeardownPlaceholder,
  type CampaignDailySnapshot,
  type CampaignDailyReplyRow,
  type CampaignDailyLeadRow,
  type CampaignDailyFirmRow,
} from '../../api';

// @section 2 AZ-date helpers (no Luxon dependency; we treat AZ as fixed UTC-7)
function todayAzString(): string {
  // America/Phoenix is fixed UTC-7 (no DST). Use the Phoenix offset directly so
  // the picker max attribute lines up with the server's clamp.
  const now = new Date();
  const az = new Date(now.getTime() - 7 * 60 * 60 * 1000);
  return az.toISOString().slice(0, 10);
}

function prettyDateAz(yyyyMmDd: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(yyyyMmDd)) return yyyyMmDd;
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString([], {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    timeZone: 'UTC',
  });
}

function relativeLabel(yyyyMmDd: string): string {
  const today = todayAzString();
  if (yyyyMmDd === today) return 'today (partial)';
  const [ty, tm, td] = today.split('-').map(Number);
  const [py, pm, pd] = yyyyMmDd.split('-').map(Number);
  const t = Date.UTC(ty, tm - 1, td);
  const p = Date.UTC(py, pm - 1, pd);
  const days = Math.round((t - p) / 86_400_000);
  if (days === 1) return 'yesterday';
  if (days > 1) return `${days} days ago`;
  return '';
}

// @section 3 Main route
export default function Daily() {
  const [picked, setPicked] = useState<string>('');           // '' = let server pick
  const [snap, setSnap] = useState<CampaignDailySnapshot | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [unconfigured, setUnconfigured] = useState(false);
  const [loading, setLoading] = useState(true);

  const todayAz = useMemo(() => todayAzString(), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    getCampaignDailySnapshot(picked || undefined)
      .then((data) => { if (!cancelled) { setSnap(data); setUnconfigured(false); } })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof CampaignsUnconfigured) setUnconfigured(true);
        else setErr(String(e));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [picked]);

  const effective = snap?.effective_date ?? picked ?? '';

  return (
    <div className="md:-mx-10 md:-my-8">
      <section className="bg-ink-950 text-paper-100 min-h-screen font-sans">
        <div className="px-4 py-5 md:px-10 md:py-7 max-w-[1400px] mx-auto">

          {/* @subsection 3.1 Breadcrumb + live badge */}
          <div className="flex items-center justify-between text-xs mb-6">
            <div className="text-muted">
              <span className="text-paper-100">Atlas</span>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100/80">Campaigns</span>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100">Daily</span>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-ok">
              <span className="w-1.5 h-1.5 rounded-full bg-ok animate-pulse"></span>
              {unconfigured ? 'Pipeline not configured' : 'Live read · AZ business day'}
            </div>
          </div>

          {/* @subsection 3.2 Title + date picker */}
          <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6 mb-8">
            <div className="flex-shrink-0">
              <div className="text-[11px] uppercase tracking-[3px] text-muted mb-2">
                Vertis Group · Daily Snapshot
              </div>
              <h1 className="text-3xl md:text-6xl font-bold tracking-tight text-paper-100 mb-2">
                {effective ? prettyDateAz(effective) : 'Loading…'}
              </h1>
              <p className="text-base text-muted md:whitespace-nowrap">
                {effective
                  ? `Arizona time · ${relativeLabel(effective) || 'selected day'}`
                  : 'Resolving latest day with data…'}
              </p>
            </div>
            <div className="flex-1 flex items-end justify-end gap-4">
              <CalendarPicker
                value={picked || (snap?.effective_date ?? '')}
                max={todayAz}
                onChange={setPicked}
              />
              {picked && (
                <button
                  type="button"
                  onClick={() => setPicked('')}
                  className="text-xs text-muted hover:text-paper-100 underline"
                >
                  reset to latest
                </button>
              )}
            </div>
          </div>

          {/* @subsection 3.3 Unconfigured banner */}
          {unconfigured && (
            <div className="rounded-xl border-2 border-watch/40 bg-ink-900 p-5 text-sm text-watch ring-1 ring-watch/10 mb-6">
              <div className="font-semibold mb-1">Campaigns pipeline isn't configured</div>
              <div className="text-muted">
                Backend returned <code className="text-watch">503 campaigns_unconfigured</code>.
              </div>
            </div>
          )}
          {err && (
            <div className="rounded-xl border-2 border-stall/40 bg-ink-900 p-5 text-sm text-stall ring-1 ring-stall/10 mb-6">
              {err}
            </div>
          )}

          {/* @subsection 3.4 Cards grid */}
          <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
            <ColdEmailCard data={snap?.cold_email ?? null} loading={loading} />
            <Ga4EngagementCard data={snap?.ga4_engagement ?? null} loading={loading} />
            <SocialCard data={snap?.social ?? null} loading={loading} />
            <WebsitesCard data={snap?.websites ?? null} loading={loading} />
          </div>

        </div>
      </section>
    </div>
  );
}

// @section 4 Cards

// @block 4.1 Cold Email
function ColdEmailCard({
  data, loading,
}: { data: CampaignDailySnapshot['cold_email']; loading: boolean }) {
  return (
    <Card title="📧 Cold Email" subtitle="Sends, opens, replies (human vs other)">
      {loading && !data ? <CardSkeleton cols={4} /> : !data ? <CardEmpty text="No sends on this day." /> : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi label="Sent" value={data.sent} tone="paper-100" />
            <Kpi label="Unique opens" value={data.opens} tone="accent-400"
                 help={`${data.open_pct.toFixed(1)}% open rate`} />
            <KpiWithDrill label="Human replies" value={data.human_replies} tone="ok"
                 help={`${data.human_reply_pct.toFixed(2)}% of sent`}
                 popoverTitle="Who replied (human)"
                 emptyText="No human replies on this day."
                 list={data.human_reply_list}
                 renderRow={renderReplyRow} />
            <KpiWithDrill label="Other replies" value={data.other_replies} tone="stall"
                 help={`${data.other_reply_pct.toFixed(2)}% of sent`}
                 popoverTitle="Other / auto replies"
                 emptyText="No other replies on this day."
                 list={data.other_reply_list}
                 renderRow={renderReplyRow} />
          </div>
          <div className="mt-4 text-[11px] text-muted leading-relaxed">
            <span className="text-paper-100/80">Other replies</span>{' '}
            ({fmt(data.other_replies)}) breaks down to{' '}
            {fmt(data.breakdown.auto_ooo)} OOO,{' '}
            {fmt(data.breakdown.auto_other)} auto-other,{' '}
            {fmt(data.breakdown.unclassified)} unclassified,{' '}
            {fmt(data.breakdown.unattributed)} unattributed.
          </div>
        </>
      )}
    </Card>
  );
}

// @block 4.2 GA4 Engagement (Demo live + Teardown placeholder)
function Ga4EngagementCard({
  data, loading,
}: { data: CampaignDailySnapshot['ga4_engagement']; loading: boolean }) {
  return (
    <Card title="📈 GA4 Engagement" subtitle="Demo (demo4) + Teardown (audit, per-firm)">
      {loading && !data ? <CardSkeleton /> : (
        <>
          <SubBlockHeader label="Demo" sublabel="demo4.optiq-os.com" />
          {!data?.demo ? (
            <CardEmpty text="No demo sessions on this day." />
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <Kpi label="Human visits" value={data.demo.human_visits} tone="ok"
                   help={`${data.demo.human_pct.toFixed(1)}% of total`} />
              <Kpi label="Other visits" value={data.demo.other_visits} tone="stall"
                   help={`${data.demo.other_pct.toFixed(1)}% bot / unknown`} />
              <KpiWithDrill label="Distinct leads" value={data.demo.distinct_human_leads} tone="accent-400"
                   help={`${data.demo.human_lead_pct.toFixed(1)}% of human visits`}
                   popoverTitle="Distinct human leads on demo4"
                   emptyText="No pid-matched leads on this day."
                   list={data.demo.lead_list}
                   renderRow={renderLeadRow} />
            </div>
          )}

          <div className="mt-5">
            <SubBlockHeader label="Teardown" sublabel="audit.optiq-os.com (per firm)" />
            {isTeardownPlaceholder(data?.teardown) ? (
              <PlaceholderBody
                message={data!.teardown.message}
                followUp="Wire ConnectionStrings--InternalWarehouseDb on this host to populate."
              />
            ) : data?.teardown && (data.teardown.human_visits + data.teardown.other_visits + data.teardown.distinct_firms_total > 0) ? (
              <div className="grid grid-cols-3 gap-3">
                <Kpi label="Human visits" value={data.teardown.human_visits} tone="ok"
                     help={`${data.teardown.human_pct.toFixed(1)}% of total`} />
                <Kpi label="Other visits" value={data.teardown.other_visits} tone="stall"
                     help={`${data.teardown.other_pct.toFixed(1)}% bot / unknown`} />
                <KpiWithDrill label="Distinct firms" value={data.teardown.distinct_firms_human} tone="accent-400"
                     help={`of ${fmt(data.teardown.distinct_firms_total)} firms hit (${data.teardown.human_firm_pct.toFixed(1)}%)`}
                     popoverTitle="Firms with human teardown visits"
                     emptyText="No firms with human visits on this day."
                     list={data.teardown.firm_list}
                     renderRow={renderFirmRow} />
              </div>
            ) : (
              <CardEmpty text="No teardown traffic on this day (BQ ingestion lags ~2 days)." />
            )}
          </div>

          <div className="mt-4 text-[11px] text-muted leading-relaxed">
            Demo and Teardown share the canonical IP-geo classifier (datacenter
            blocklist + business-state match against the lead). Teardown firm
            is extracted from the cold-email link's ?firm= query param. The
            nightly classified view rebuilds at 12 UTC; intra-day reads fall
            back to a lighter live BQ classifier (no state-match). Drill-throughs
            (by cohort, by lead) live on the existing GA4 tab.
          </div>
        </>
      )}
    </Card>
  );
}

function SubBlockHeader({ label, sublabel }: { label: string; sublabel: string }) {
  return (
    <div className="flex items-baseline gap-2 mb-2">
      <span className="text-[10px] uppercase tracking-[2px] text-paper-100/80 font-semibold">{label}</span>
      <span className="text-[10px] text-muted">{sublabel}</span>
    </div>
  );
}

// @block 4.3 Social (placeholder)
function SocialCard({
  data, loading,
}: { data: CampaignDailySnapshot['social'] | null; loading: boolean }) {
  return (
    <Card title="📣 Social" subtitle="Posts published, channel visits">
      {loading && !data ? <CardSkeleton /> : (
        <PlaceholderBody
          message={data?.message ?? 'Not wired yet.'}
          followUp="Activate when post events log to a queryable table (LinkedIn Company Pages via Bridge first)."
        />
      )}
    </Card>
  );
}

// @block 4.4 Marketing Websites (placeholder)
function WebsitesCard({
  data, loading,
}: { data: CampaignDailySnapshot['websites'] | null; loading: boolean }) {
  return (
    <Card title="🌐 Marketing Websites" subtitle="signal-rev, navipoint-technologies, vertis-group, vertisholdings">
      {loading && !data ? <CardSkeleton /> : (
        <PlaceholderBody
          message={data?.message ?? 'Not wired yet.'}
          followUp="Enable GA4 → BigQuery export on the 4 properties; this card will read raw.ga4_bq_events by event_date once they land."
        />
      )}
    </Card>
  );
}

// @section 5 Primitives

function Card({
  title, subtitle, children,
}: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-5">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-lg font-bold text-paper-100">{title}</h2>
        {subtitle && <span className="text-[11px] text-muted">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

function Kpi({ label, value, tone, help }: {
  label: string; value: number; tone: string; help?: string;
}) {
  return (
    <div className="rounded-lg border border-ink-800 bg-ink-950/40 p-3">
      <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold">{label}</div>
      <div className={`text-3xl font-bold tabular-nums mt-1 text-${tone}`}>{fmt(value)}</div>
      {help && <div className="text-[11px] text-muted mt-0.5">{help}</div>}
    </div>
  );
}

function PlaceholderBody({ message, followUp }: { message: string; followUp: string }) {
  return (
    <div className="rounded-lg border border-dashed border-ink-700 bg-ink-950/30 p-5">
      <div className="text-sm text-muted mb-2">
        <span className="inline-block px-2 py-0.5 rounded bg-watch/15 text-watch text-[10px] font-semibold uppercase tracking-wide mr-2">
          TBD
        </span>
        {message}
      </div>
      <div className="text-[11px] text-muted/80">{followUp}</div>
    </div>
  );
}

function CardSkeleton({ cols = 3 }: { cols?: number }) {
  const slots = Array.from({ length: cols }, (_, i) => i);
  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
      {slots.map((i) => (
        <div key={i} className="rounded-lg border border-ink-800 bg-ink-950/40 p-3 animate-pulse">
          <div className="h-2 w-12 bg-ink-800 rounded mb-3"></div>
          <div className="h-6 w-16 bg-ink-800 rounded"></div>
        </div>
      ))}
    </div>
  );
}

function CardEmpty({ text }: { text: string }) {
  return <div className="text-sm text-muted py-6 text-center">{text}</div>;
}

// @section 6 Formatting
function fmt(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '-';
  return n.toLocaleString();
}

// @section 7 Calendar picker (popover, no extra deps)
// Click button → month-view calendar opens; click date → select + close.
// Future dates (> max) disabled. ESC or outside-click closes.

function CalendarPicker({
  value, max, onChange,
}: { value: string; max: string; onChange: (yyyyMmDd: string) => void }) {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<{ y: number; m: number }>(() => {
    const ref = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : max;
    const [y, m] = ref.split('-').map(Number);
    return { y, m };
  });
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const display = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? prettyDateAz(value) : 'Pick a day';
  const maxDateOrdinal = ymdToOrdinal(max);
  const valueOrdinal = value ? ymdToOrdinal(value) : -1;
  const [todayY, todayM, todayD] = todayAzString().split('-').map(Number);
  const todayOrdinal = ymdToOrdinal(todayAzString());

  const monthLabel = new Date(Date.UTC(viewMonth.y, viewMonth.m - 1, 1))
    .toLocaleDateString([], { month: 'long', year: 'numeric', timeZone: 'UTC' });

  const grid = buildMonthGrid(viewMonth.y, viewMonth.m);

  return (
    <div ref={wrapRef} className="relative flex flex-col gap-1">
      <label className="text-[10px] uppercase tracking-[2px] text-muted font-semibold">
        Pick a day
      </label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="bg-ink-900 border border-ink-800 rounded-lg px-3 py-2 text-sm text-paper-100 min-w-[220px] text-left hover:border-ink-700 transition-colors flex items-center justify-between gap-2"
      >
        <span>{display}</span>
        <span className="text-muted text-xs">📅</span>
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+6px)] z-50 bg-ink-900 border border-ink-800 rounded-xl p-3 shadow-2xl shadow-black/40 w-[280px]">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setViewMonth((v) => prevMonth(v))}
              className="text-paper-100 hover:bg-ink-800 rounded px-2 py-0.5 text-sm"
              aria-label="Previous month"
            >
              ‹
            </button>
            <span className="text-sm font-semibold text-paper-100">{monthLabel}</span>
            <button
              type="button"
              onClick={() => setViewMonth((v) => nextMonth(v))}
              className="text-paper-100 hover:bg-ink-800 rounded px-2 py-0.5 text-sm"
              aria-label="Next month"
            >
              ›
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wide text-muted text-center mb-1">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1 text-xs">
            {grid.map((cell, i) => {
              if (!cell) return <div key={i} />;
              const isFuture = cell.ordinal > maxDateOrdinal;
              const isSelected = cell.ordinal === valueOrdinal;
              const isToday = cell.ordinal === todayOrdinal;
              const isOutsideMonth = cell.y !== viewMonth.y || cell.m !== viewMonth.m;
              return (
                <button
                  key={i}
                  type="button"
                  disabled={isFuture}
                  onClick={() => {
                    onChange(cell.iso);
                    setOpen(false);
                  }}
                  className={[
                    'aspect-square rounded text-center tabular-nums',
                    isFuture ? 'text-muted/40 cursor-not-allowed' : 'hover:bg-ink-800 text-paper-100 cursor-pointer',
                    isOutsideMonth && !isFuture ? 'text-muted' : '',
                    isSelected ? 'bg-accent-400/20 ring-1 ring-accent-400 text-paper-100' : '',
                    isToday && !isSelected ? 'ring-1 ring-ok/40' : '',
                  ].join(' ')}
                >
                  {cell.d}
                </button>
              );
            })}
          </div>
          <div className="mt-2 pt-2 border-t border-ink-800 flex items-center justify-between text-[10px] text-muted">
            <span>{todayY}-{String(todayM).padStart(2, '0')}-{String(todayD).padStart(2, '0')} = today (AZ)</span>
            <button
              type="button"
              onClick={() => { setViewMonth({ y: todayY, m: todayM }); }}
              className="hover:text-paper-100 underline"
            >
              jump to today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ymdToOrdinal(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return Date.UTC(y, m - 1, d) / 86_400_000;
}
function prevMonth(v: { y: number; m: number }) {
  return v.m === 1 ? { y: v.y - 1, m: 12 } : { y: v.y, m: v.m - 1 };
}
function nextMonth(v: { y: number; m: number }) {
  return v.m === 12 ? { y: v.y + 1, m: 1 } : { y: v.y, m: v.m + 1 };
}
function buildMonthGrid(y: number, m: number): Array<{ y: number; m: number; d: number; iso: string; ordinal: number } | null> {
  // Sunday-first grid, 6 rows × 7 cols, filled with adjacent-month days for completeness.
  const first = new Date(Date.UTC(y, m - 1, 1));
  const firstDow = first.getUTCDay(); // 0 = Sun
  const startUtcMs = first.getTime() - firstDow * 86_400_000;
  return Array.from({ length: 42 }, (_, i) => {
    const dt = new Date(startUtcMs + i * 86_400_000);
    const cy = dt.getUTCFullYear();
    const cm = dt.getUTCMonth() + 1;
    const cd = dt.getUTCDate();
    const iso = `${cy}-${String(cm).padStart(2, '0')}-${String(cd).padStart(2, '0')}`;
    return { y: cy, m: cm, d: cd, iso, ordinal: dt.getTime() / 86_400_000 };
  });
}

// @section 8 KPI with click-to-show drill popover

function KpiWithDrill<T>({
  label, value, tone, help,
  popoverTitle, emptyText, list, renderRow,
}: {
  label: string;
  value: number;
  tone: string;
  help?: string;
  popoverTitle: string;
  emptyText: string;
  list: T[] | undefined;
  renderRow: (row: T, i: number) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const rows = list ?? [];
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left rounded-lg border border-ink-800 bg-ink-950/40 p-3 hover:border-accent-400/40 transition-colors cursor-pointer"
        title="Click to see who's in this group"
      >
        <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold flex items-center justify-between">
          <span>{label}</span>
          <span className="text-[9px] text-muted/60 normal-case tracking-normal">click ⌄</span>
        </div>
        <div className={`text-3xl font-bold tabular-nums mt-1 text-${tone}`}>{fmt(value)}</div>
        {help && <div className="text-[11px] text-muted mt-0.5">{help}</div>}
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-40 bg-ink-900 border border-ink-800 rounded-xl shadow-2xl shadow-black/40 max-h-[360px] overflow-auto">
          <div className="sticky top-0 bg-ink-900 border-b border-ink-800 px-3 py-2 flex items-baseline justify-between">
            <span className="text-xs font-semibold text-paper-100">{popoverTitle}</span>
            <span className="text-[10px] text-muted">{rows.length} {rows.length === 1 ? 'row' : 'rows'}</span>
          </div>
          {rows.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-muted">{emptyText}</div>
          ) : (
            <div className="divide-y divide-ink-800">
              {rows.map((row, i) => (
                <div key={i} className="px-3 py-2 text-xs hover:bg-ink-800/40">
                  {renderRow(row, i)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// @section 9 Drill-row renderers

function renderReplyRow(row: CampaignDailyReplyRow, _i: number): ReactNode {
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <span className="text-paper-100 font-medium truncate">{row.from_address ?? '(unknown sender)'}</span>
        {row.classification && (
          <span className="text-[10px] text-muted whitespace-nowrap">{row.classification}</span>
        )}
      </div>
      {row.subject && <div className="text-muted truncate">{row.subject}</div>}
    </div>
  );
}

function renderLeadRow(row: CampaignDailyLeadRow, _i: number): ReactNode {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-paper-100 truncate">
        {row.firm_key ?? row.lead_id.slice(0, 8)}
        {row.lead_state && <span className="text-muted ml-2">{row.lead_state}</span>}
      </span>
      <span className="text-muted whitespace-nowrap tabular-nums">{row.dwell_sec}s dwell</span>
    </div>
  );
}

function renderFirmRow(row: CampaignDailyFirmRow, _i: number): ReactNode {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-paper-100 truncate">{row.firm_domain}</span>
      <span className="text-muted whitespace-nowrap tabular-nums">
        <span className="text-ok">{row.human_visits} human</span>
        {row.other_visits > 0 && <span className="ml-2 text-stall">{row.other_visits} other</span>}
      </span>
    </div>
  );
}
